import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActivityAction, ActivityItem } from 'app/data/types/activity';
import { OPEN_MODES, OpenMode, TRANSACTION_PATH, TRANSACTION_SEGMENT } from 'app/shared/consts';
import { useActivityDeeplink } from 'app/shared/hooks/use-activity-deeplink';
import { useBookAgain } from 'app/shared/hooks/use-book-again';
import { useOpenParams } from 'app/shared/hooks/use-open-params';
import { toDestinationRoute } from 'app/shared/hooks/use-activity-tracking';
import { getRecordAction } from 'app/shared/utils/build-activity-deeplink';

/**
 * Where a tap goes and how it gets there — resolved once, then used for BOTH the navigation and the
 * telemetry payload.
 *
 * Some tiles and CTAs open a screen inside Activity Center, others hand off to the source miniapp, and
 * the mix differs per activity type and per action. When each call site wrote its own `open_mode`
 * literal next to its own navigation branch, the two could disagree silently — a CTA switched from a
 * deeplink to an in-app screen would keep reporting `deeplink` until somebody remembered the tracking
 * call. Returning the mode, the destination and the navigation together makes that impossible.
 */
export interface ActivityTarget {
  mode: OpenMode;
  /** Normalised for tracking (origin + path, no query), or null when the destination isn't knowable. */
  destinationRoute: string | null;
  /** Performs the navigation this target describes. */
  open: () => void;
}

/**
 * The in-app detail id for an item the in-app screen still serves.
 *
 * ⛔ RESOURCE BOOKINGS ARE NOT ON IT ANY MORE. A booking now routes OUT to the resource-booking app
 * the way an event always has — its own bookings page where the host gives a record a page, the
 * single booking in a column. That app owns the booking: it draws add-ons, messages and payment,
 * none of which the in-app screen ever had, and a second detail view of the same record is how the
 * two drift.
 *
 * Events keep it. Their real documents carry only an `eventUuid` and deeplink out already, so this
 * is reached only by a document that also names a transaction — and changing what those open is a
 * decision about events, not one this change gets to make on the way past.
 */
const transactionUuidOf = (item: ActivityItem, action?: ActivityAction): string | undefined =>
  (item.type === 'event' && !action
    // Shared lookup: the selection recorded for the host's pane must describe the destination this
    // picks, so both ask the same question rather than each finding the action their own way.
    ? getRecordAction(item)?.routingDetails?.transactionUuid
    : undefined);

interface UseActivityTarget {
  /** `null` when the tap can't be routed at all — the caller should do nothing and emit nothing. */
  resolveTarget: (item: ActivityItem, action?: ActivityAction) => ActivityTarget | null;
}

export const useActivityTarget = (): UseActivityTarget => {
  const navigate = useNavigate();
  const { getDeeplinkUrl, openActivityItem } = useActivityDeeplink();
  const { openBookAgain } = useBookAgain();
  /*
   * On the portal's full-page surface the host shows a record BESIDE this list, so the in-app
   * transaction screen has nothing left to do: opening it would put a detail view inside the left
   * column while the column built for details sat empty next to it. The record goes out to the host
   * instead, which is a real gain rather than a workaround — the pane frames a url with its path
   * intact, so a booking reaches the resource-booking app's own `receipt/:transactionUuid` page,
   * which is more than the in-app screen showed.
   *
   * Still read even though bookings have left the in-app screen: an event document carrying a
   * transaction takes the branch below, and the pane is the right destination for it here too.
   */
  const { hostSurface } = useOpenParams();
  const isSplitSurface = hostSurface === 'fullscreen';

  const resolveTarget = useCallback(
    (item: ActivityItem, action?: ActivityAction): ActivityTarget | null => {
      // In-app: an event document that names a transaction opens the booking screen. Navigating
      // relatively keeps the feed's query string (segment + filter) in history, so back restores
      // the pane.
      const transactionUuid = transactionUuidOf(item, action);
      if (transactionUuid && !isSplitSurface) {
        return {
          mode: OPEN_MODES.IN_APP,
          destinationRoute: TRANSACTION_PATH,
          open: () => navigate(`${TRANSACTION_SEGMENT}/${transactionUuid}`),
        };
      }

      // "Book again" resolves the resource asynchronously before deeplinking, so its exact url isn't
      // known at tap time — report the module it lands in.
      if (action?.type === 'book_again') {
        return {
          mode: OPEN_MODES.DEEPLINK,
          destinationRoute: toDestinationRoute(getDeeplinkUrl(item, action)),
          open: () => {
            openBookAgain(item).catch(() => undefined);
          },
        };
      }

      const url = getDeeplinkUrl(item, action);
      if (!url) {
        return null;
      }

      return {
        mode: OPEN_MODES.DEEPLINK,
        destinationRoute: toDestinationRoute(url),
        open: () => openActivityItem(item, action),
      };
    },
    [navigate, getDeeplinkUrl, openActivityItem, openBookAgain, isSplitSurface],
  );

  return { resolveTarget };
};
